import {useMemo, useState} from 'react';
import {useForm, type UseFormReturn} from 'react-hook-form';
import {useDispatch} from 'react-redux';

import {showToast} from '@/components/Toaster/Toaster';
import appConfig from '@/config/appConfig.typed';
import AGE_GATE_INPUTS from '@/consts/ageGateInputs';
import ageGateService from '@/services/ageGate/ageGateService';
import appContent from '@/services/content/appContent.typed';
import log from '@/services/logger/log';
import routerService from '@/services/route/routerService';
import {showAgeDeniedPopup} from '@/state/slices/modalSlice';

type AgeGateFormValues = {
    month: string;
    year: string;
};

type SelectOption = {
    value: string;
    label: string;
};

type UseAgeGateReturn = UseFormReturn<AgeGateFormValues> & {
    onSubmit: () => void;
    isLoading: boolean;
    monthOptions: SelectOption[];
    yearOptions: SelectOption[];
};

const MONTHS = Array.from({length: 12}, (_, i) => {
    const val = String(i + 1).padStart(2, '0');

    return {value: val, label: val};
});

const useAgeGate = ({onLocked}: {onLocked: () => void}): UseAgeGateReturn => {
    const {popup, notificationTitle, notificationButton} = appContent.getAgeGateDeniedContent() || {};
    const {oldestAllowedYear} = appConfig.getAgeGateSettings();

    const currentYear = new Date().getFullYear();

    const yearOptions = useMemo<SelectOption[]>(() => {
        const years: SelectOption[] = [];
        const minYear = oldestAllowedYear;

        for (let y = currentYear; y >= minYear; y--) {
            const val = String(y);

            years.push({value: val, label: val});
        }

        return years;
    }, [currentYear, oldestAllowedYear]);

    const form = useForm<AgeGateFormValues>({
        defaultValues: {
            [AGE_GATE_INPUTS.MONTH]: '',
            [AGE_GATE_INPUTS.YEAR]: '',
        },
        mode: 'onChange',
    });

    const {watch} = form;
    const [isLoading, setIsLoading] = useState(false);
    const dispatch = useDispatch();

    const onSubmit = () => {
        const month = parseInt(watch(AGE_GATE_INPUTS.MONTH), 10);
        const year = parseInt(watch(AGE_GATE_INPUTS.YEAR), 10);
        const isValid = ageGateService.validateAge(year, month);

        log.info(`Age Verification Submit: isValid: ${isValid}, year: ${year}, month: ${month}`);

        if (isValid) {
            setIsLoading(true);
            ageGateService.setIsAgeConfirmed(true);
            setTimeout(() => routerService.reload(), 0);
        } else {
            const lockedContent = appContent.getAgeGateLockedContent();
            const {lockTime} = appConfig.getAgeGateSettings();

            if (lockedContent && lockTime) {
                ageGateService.lockAgeGate();
                onLocked();
            } else if (popup) {
                dispatch(showAgeDeniedPopup());
            } else if (notificationTitle) {
                showToast(notificationTitle, {
                    ...(notificationButton?.text && {
                        action: {
                            label: notificationButton.text,
                        },
                    }),
                    duration: 4000,
                    className: 'mb-safe-offset-16 md:mb-0',
                });
            }
        }
    };

    return {
        ...form,
        onSubmit,
        isLoading,
        monthOptions: MONTHS,
        yearOptions,
    };
};

export default useAgeGate;
